import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, BookOpen, ExternalLink, MessageCircle, Plus, ShieldAlert, Sparkles, X } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { format } from 'date-fns';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';
import { Card, SectionHeader } from '@/components/Card';
import { Input } from '@/components/Input';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Switch } from '@/components/settings/SettingsPage';
import { useTheme } from '@/theme/ThemeProvider';
import { AiBotConfigCard, DEFAULT_AI_CONFIG } from '@/pages/whatsapp-account/AccountSettings';
import { whatsappAccountService, AiBotConfig } from '@/services/whatsapp-account.service';
import { aiAgentsService, AiAgent, AgentAnalytics, WhatsappChannel } from '@/services/ai-agents.service';
import { knowledgeService, KnowledgeSource } from '@/services/knowledge.service';
import { useBrainOptions } from './agentShared';

// ─── Overview ─────────────────────────────────────────────────────────────

/** Validated (dataviz validator) per surface: light #FFFFFF, dark #131316. */
const SERIES = {
  light: { ai: '#C8A55B', team: '#2F66A0' },
  dark: { ai: '#B08835', team: '#4A82C2' },
};

export function OverviewTab({ agent }: { agent: AiAgent }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<AgentAnalytics | null>(null);
  const { mode } = useTheme();
  const colors = SERIES[mode === 'dark' ? 'dark' : 'light'];
  useEffect(() => {
    setData(null);
    aiAgentsService.analytics(agent.agentUid, days).then(setData).catch(() => undefined);
  }, [agent.agentUid, days]);

  const rows = useMemo(() => (data?.series ?? []).map((d) => ({ ...d, label: format(new Date(`${d.date}T00:00:00`), days > 31 ? 'd MMM' : 'd MMM'), team: d.conversations - d.resolved })), [data, days]);
  const t = data?.totals;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-text-muted">Website and app chats. WhatsApp numbers report in WhatsApp → Accounts → Bot health.</p>
        <div className="flex rounded-xl bg-surface-alt p-1">
          {[7, 30, 90].map((d) => (
            <button key={d} type="button" onClick={() => setDays(d)} className={clsx('rounded-lg px-3 py-1.5 text-xs font-medium', days === d ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary')}>{d} days</button>
          ))}
        </div>
      </div>
      {!t ? <div className="skeleton h-72 rounded-2xl" /> : (
        <>
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <Kpi label="Conversations" value={t.conversations} hint={`${t.web} website · ${t.api} app`} />
            <Kpi label="Answered by AI" value={`${t.resolutionRate}%`} hint={`${t.resolvedByAi} with no team help`} />
            <Kpi label="Needed the team" value={t.handoffs + t.takeovers} hint={`${t.handoffs} handed off · ${t.takeovers} taken over`} />
            <Kpi label="Qualified leads" value={t.qualified} hint={`${t.leads} chats became leads`} />
            <Kpi label="AI replies" value={t.aiReplies} hint={t.aiFailed ? `${t.aiFailed} failed (holding reply sent)` : 'none failed'} tone={t.aiFailed ? 'warning' : undefined} />
            <Kpi label="Unanswered questions" value={t.unanswered} hint="in Knowledge → Unanswered" tone={t.unanswered ? 'warning' : undefined} link={t.unanswered ? '/ai-agents/knowledge?tab=gaps' : undefined} />
            <Kpi label="AI credits used" value={t.credits} hint={t.conversations ? `${(t.credits / t.conversations).toFixed(2)} per chat` : '—'} />
            <Kpi label="WhatsApp numbers" value={t.whatsappNumbers} hint="answered by this agent" link={`/ai-agents/${agent.agentUid}?tab=whatsapp`} />
          </div>
          <Card>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-semibold text-text-primary">Conversations per day</div>
              <div className="flex gap-4 text-xs text-text-secondary">
                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: colors.ai }} />Answered by AI · {t.resolvedByAi}</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: colors.team }} />Needed the team · {t.conversations - t.resolvedByAi}</span>
              </div>
            </div>
            {t.conversations === 0 ? (
              <div className="flex h-48 items-center justify-center text-sm text-text-muted">No chats in the last {days} days.</div>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={rows} margin={{ top: 4, right: 4, left: -24, bottom: 0 }} barCategoryGap="25%">
                    <CartesianGrid vertical={false} stroke="var(--color-border)" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={16} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} tickLine={false} axisLine={false} />
                    <Tooltip cursor={{ fill: 'var(--color-surface-alt)' }} contentStyle={{ borderRadius: 12, border: '1px solid var(--color-border)', background: 'var(--color-surface)', fontSize: 12, color: 'var(--color-text-primary)' }} />
                    <Bar dataKey="resolved" name="Answered by AI" stackId="a" fill={colors.ai} stroke="var(--color-surface)" strokeWidth={1} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="team" name="Needed the team" stackId="a" fill={colors.team} stroke="var(--color-surface)" strokeWidth={1} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, hint, tone, link }: { label: string; value: string | number; hint?: string; tone?: 'warning'; link?: string }) {
  const body = (
    <>
      <div className="text-xs font-medium text-text-muted">{label}</div>
      <div className={clsx('mt-1 truncate text-xl font-semibold', tone === 'warning' ? 'text-warning' : 'text-text-primary')}>{value}</div>
      {hint && <div className="mt-0.5 truncate text-xs text-text-muted" title={hint}>{hint}</div>}
    </>
  );
  return link
    ? <Link to={link} className="min-w-0 rounded-2xl border border-border bg-surface p-4 transition-colors hover:border-primary/50">{body}</Link>
    : <div className="min-w-0 rounded-2xl border border-border bg-surface p-4">{body}</div>;
}

// ─── Persona & knowledge ──────────────────────────────────────────────────

export function PersonaTab({ agent, isAdmin, onSaved }: { agent: AiAgent; isAdmin: boolean; onSaved: (a: AiAgent) => void }) {
  const { options, loading } = useBrainOptions();
  const [brain, setBrain] = useState(agent.brainAccountUid ?? '');
  const [persona, setPersona] = useState<AiBotConfig>(agent.persona ?? DEFAULT_AI_CONFIG);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setBrain(agent.brainAccountUid ?? ''); setPersona(agent.persona ?? DEFAULT_AI_CONFIG); }, [agent]);
  const borrowedLabel = options.find((o) => o.value === agent.brainAccountUid)?.label;

  const save = async (patch: Parameters<typeof aiAgentsService.update>[1], ok = 'Saved') => {
    setBusy(true);
    try { onSaved(await aiAgentsService.update(agent.agentUid, patch)); toast.success(ok); }
    catch (e: any) { if (!e?.response) toast.error('Could not save'); }
    finally { setBusy(false); }
  };
  /** Start the agent's own persona from the number it was borrowing, so nothing is lost. */
  const makeOwn = async () => {
    setBusy(true);
    try {
      const acc = await whatsappAccountService.getById(agent.brainAccountUid!);
      onSaved(await aiAgentsService.update(agent.agentUid, { brainAccountUid: '', persona: acc.aiBotConfig ?? DEFAULT_AI_CONFIG }));
      toast.success('Copied — this agent now has its own persona');
    } catch (e: any) { if (!e?.response) toast.error('Could not copy the setup'); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl space-y-5">
      <Card>
        <SectionHeader title="Who it answers like" icon={<Sparkles size={16} />} className="mb-3" subtitle="Give the agent its own persona, or reuse the AI setup of one of your WhatsApp numbers." />
        <div className="flex flex-wrap gap-2">
          <div className="min-w-[16rem] flex-1">
            <SearchableSelect options={options} value={brain} onChange={setBrain} disabled={!isAdmin || loading} numbered={false} searchable={options.length > 6} aria-label="Who it answers like" />
          </div>
          {isAdmin && brain !== (agent.brainAccountUid ?? '') && <Button loading={busy} onClick={() => save({ brainAccountUid: brain })}>Save</Button>}
        </div>
        {agent.brainAccountUid && (
          <div className="mt-4 rounded-xl border border-border bg-surface-alt/60 p-3 text-sm text-text-secondary">
            Persona, services, prices and the details it collects come from <b className="text-text-primary">{borrowedLabel ?? 'that number'}</b>'s WhatsApp AI settings.
            <div className="mt-2 flex flex-wrap gap-2">
              <Link to={`/whatsapp-accounts?tab=profile&account=${agent.brainAccountUid}`}><Button variant="secondary" size="sm" iconLeft={<ExternalLink size={13} />}>Edit on that number</Button></Link>
              {isAdmin && <Button variant="ghost" size="sm" loading={busy} onClick={makeOwn}>Give it its own persona instead</Button>}
            </div>
          </div>
        )}
      </Card>
      {!agent.brainAccountUid && (
        <AiBotConfigCard
          cfg={persona}
          onChange={setPersona}
          busy={busy || !isAdmin}
          onSave={() => save({ persona }, 'Persona saved')}
          subtitle="How this agent talks, what it offers and what it collects — on your website, in your app and on its WhatsApp numbers."
        />
      )}
      <HandoffCard agent={agent} isAdmin={isAdmin} onSaved={onSaved} />
      <KnowledgeCard agent={agent} isAdmin={isAdmin} onSaved={onSaved} />
    </div>
  );
}

const KEYWORD_IDEAS = ['refund', 'complaint', 'manager', 'speak to a person', 'cancel my order'];

function HandoffCard({ agent, isAdmin, onSaved }: { agent: AiAgent; isAdmin: boolean; onSaved: (a: AiAgent) => void }) {
  const [words, setWords] = useState<string[]>(agent.handoffKeywords ?? []);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(words) !== JSON.stringify(agent.handoffKeywords ?? []);
  const add = (raw: string) => {
    const parts = raw.split(',').map((w) => w.trim().toLowerCase()).filter(Boolean);
    if (parts.length) setWords((w) => Array.from(new Set([...w, ...parts])).slice(0, 30));
    setText('');
  };
  const save = async () => {
    setSaving(true);
    try { onSaved(await aiAgentsService.update(agent.agentUid, { handoffKeywords: words })); toast.success('Saved'); }
    catch (e: any) { if (!e?.response) toast.error('Could not save'); }
    finally { setSaving(false); }
  };
  return (
    <Card>
      <SectionHeader title="Always hand over to a person" icon={<ShieldAlert size={16} />} className="mb-3" subtitle="When a customer's message contains one of these words or phrases, the AI doesn't reply — the chat goes straight to your team, on every channel." />
      <div className="flex gap-2">
        <div className="min-w-0 flex-1"><Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(text); } }} placeholder="refund, complaint, speak to a manager" disabled={!isAdmin} aria-label="Add a word or phrase" /></div>
        <Button variant="secondary" iconLeft={<Plus size={14} />} disabled={!isAdmin || !text.trim()} onClick={() => add(text)}>Add</Button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {words.map((w) => (
          <span key={w} className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-alt py-1 pl-3 pr-1.5 text-xs font-medium text-text-primary">
            {w}
            {isAdmin && <button type="button" onClick={() => setWords((x) => x.filter((y) => y !== w))} aria-label={`Remove ${w}`} className="rounded-full p-0.5 text-text-muted hover:bg-danger-soft hover:text-danger"><X size={12} /></button>}
          </span>
        ))}
        {isAdmin && KEYWORD_IDEAS.filter((k) => !words.includes(k)).map((k) => (
          <button key={k} type="button" onClick={() => add(k)} className="rounded-full border border-dashed border-border px-3 py-1 text-xs text-text-muted hover:border-primary/50 hover:text-text-primary">+ {k}</button>
        ))}
      </div>
      {isAdmin && <div className="mt-3 flex justify-end"><Button disabled={!dirty} loading={saving} onClick={save}>Save</Button></div>}
    </Card>
  );
}

// ─── WhatsApp ─────────────────────────────────────────────────────────────

export function WhatsappTab({ agent, isAdmin }: { agent: AiAgent; isAdmin: boolean }) {
  const [rows, setRows] = useState<WhatsappChannel[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => { aiAgentsService.whatsappChannels(agent.agentUid).then(setRows).catch(() => setRows([])); }, [agent.agentUid]);

  const toggle = async (row: WhatsappChannel, enabled: boolean) => {
    if (enabled && row.otherAgent && !window.confirm(`${row.phone} is answered by "${row.otherAgent.name}". Move it to "${agent.name}"?`)) return;
    setBusy(row.accountUid);
    try {
      setRows(await aiAgentsService.setWhatsappChannel(agent.agentUid, row.accountUid, enabled));
      toast.success(enabled ? `${agent.name} now answers ${row.phone}` : `${row.phone} is back on its own AI setup`);
    } catch (e: any) { if (!e?.response) toast.error('Could not change it'); }
    finally { setBusy(null); }
  };

  return (
    <div className="max-w-4xl space-y-4">
      <p className="text-sm text-text-muted">
        On these numbers the agent's persona, knowledge and hand-over rules replace the number's own AI settings — one brain on every channel.
        WhatsApp's own rules still apply (24-hour window, opt-outs, turn limits), and the chats stay in WhatsApp → Chats.
      </p>
      {rows === null ? <div className="skeleton h-32 rounded-2xl" /> : rows.length === 0 ? (
        <Card><div className="text-sm text-text-muted">No WhatsApp numbers connected yet. <Link to="/whatsapp-accounts" className="font-medium text-primary hover:underline">Connect one</Link>.</div></Card>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border bg-surface">
          {rows.map((r) => (
            <div key={r.accountUid} className="border-b border-border px-4 py-3 last:border-0">
              <div className="flex flex-wrap items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-success-soft text-success"><MessageCircle size={16} /></span>
                <div className="min-w-[12rem] flex-1">
                  <div className="text-sm font-semibold text-text-primary">{r.businessName}</div>
                  <div className="text-xs text-text-muted">
                    {r.phone} · {r.botMode === 'ai_chat' ? 'AI chat' : r.botMode === 'flow' ? 'Scripted flows' : 'Bot off'}
                    {!r.isActive && ' · not in use'}
                    {r.otherAgent && <> · answered by <b className="text-text-secondary">{r.otherAgent.name}</b></>}
                  </div>
                </div>
                <Switch checked={r.assigned} disabled={!isAdmin || busy === r.accountUid} label={`Answer ${r.phone} with ${agent.name}`} onChange={(v) => toggle(r, v)} />
              </div>
              {r.assigned && r.botMode !== 'ai_chat' && (
                <p className="mt-2 flex items-start gap-1.5 text-xs text-warning">
                  <AlertTriangle size={13} className="mt-px shrink-0" />
                  This number isn't in AI chat mode, so the agent won't reply there yet.&nbsp;
                  <Link to={`/whatsapp-accounts?tab=profile&account=${r.accountUid}`} className="font-medium underline">Switch it to AI chat</Link>
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KnowledgeCard({ agent, isAdmin, onSaved }: { agent: AiAgent; isAdmin: boolean; onSaved: (a: AiAgent) => void }) {
  const initial = agent.knowledge ?? { mode: 'all' as const, sourceUids: [] };
  const [mode, setMode] = useState(initial.mode);
  const [picked, setPicked] = useState<string[]>(initial.sourceUids);
  const [sources, setSources] = useState<KnowledgeSource[] | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { knowledgeService.sources().then(setSources).catch(() => setSources([])); }, []);
  const dirty = mode !== initial.mode || JSON.stringify([...picked].sort()) !== JSON.stringify([...initial.sourceUids].sort());
  const save = async () => {
    setSaving(true);
    try { onSaved(await aiAgentsService.update(agent.agentUid, { knowledge: { mode, sourceUids: mode === 'selected' ? picked : [] } })); toast.success('Saved'); }
    catch (e: any) { if (!e?.response) toast.error('Could not save'); }
    finally { setSaving(false); }
  };
  const options: Array<{ id: typeof mode; label: string }> = [{ id: 'all', label: 'All sources' }, { id: 'selected', label: 'Only some' }, { id: 'off', label: 'Off' }];
  return (
    <Card>
      <SectionHeader title="Knowledge" icon={<BookOpen size={16} />} className="mb-3" subtitle="Website pages, files and Q&As the agent answers from. It cites what it used and lists what it couldn't answer." />
      <div className="inline-flex rounded-xl border border-border bg-surface-alt p-1">
        {options.map((o) => (
          <button key={o.id} type="button" disabled={!isAdmin} onClick={() => setMode(o.id)}
            className={clsx('rounded-lg px-4 py-1.5 text-xs font-medium transition-colors', mode === o.id ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary')}>
            {o.label}
          </button>
        ))}
      </div>
      {sources !== null && sources.length === 0 && (
        <p className="mt-3 text-xs text-text-muted">Your knowledge base is empty. <Link to="/ai-agents/knowledge" className="font-medium text-primary hover:underline">Add your website or a file</Link> so the agent can answer more than the basics.</p>
      )}
      {mode === 'selected' && !!sources?.length && (
        <div className="mt-3 max-h-64 space-y-1 overflow-y-auto rounded-xl border border-border p-2">
          {sources.map((s) => {
            const on = picked.includes(s.sourceUid);
            return (
              <label key={s.sourceUid} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-surface-alt">
                <input type="checkbox" className="accent-[var(--color-primary)]" checked={on} disabled={!isAdmin}
                  onChange={() => setPicked((p) => (on ? p.filter((x) => x !== s.sourceUid) : [...p, s.sourceUid]))} />
                <span className="min-w-0 flex-1 truncate text-text-primary">{s.title}</span>
                <span className="text-[11px] text-text-muted">{s.type === 'faq' ? 'Q&A' : s.type}</span>
              </label>
            );
          })}
        </div>
      )}
      {mode === 'selected' && picked.length === 0 && !!sources?.length && <p className="mt-2 text-xs text-warning">Pick at least one source — with none picked the agent uses no knowledge.</p>}
      {isAdmin && (
        <div className="mt-3 flex justify-end">
          <Button disabled={!dirty} loading={saving} onClick={save}>Save</Button>
        </div>
      )}
    </Card>
  );
}
